import React, { useState } from 'react';
import { useStudioStore } from '../../stores/useStudioStore';
import { studioApiService } from '../../services/studioApiService';

export const UpscalerPage: React.FC = () => {
  const { 
    replicateKey, setReplicateKey,
    upscalerQueue, toggleUpscalerSelect, selectAllUpscaler,
    updateUpscalerItem, clearUpscalerQueue, addDbUpload
  } = useStudioStore();

  const [showRep, setShowRep] = useState(false);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const selectedItems = upscalerQueue.filter(i => i.selected);

  const handleUpscaleSelected = async () => {
    if (!replicateKey) {
      alert("Please enter a Replicate API key for 4× upscaling.");
      return;
    }
    const pendingSelected = upscalerQueue.filter(i => i.selected && i.status !== 'complete');
    if (pendingSelected.length === 0) return;

    setIsUpscaling(true);

    for (const item of pendingSelected) {
      updateUpscalerItem(item.id, { status: 'upscaling' });
      
      try {
        const res = await studioApiService.upscaleImage(replicateKey, item.fullSrc);
        const predictionId = res.id;
        
        // Poll for completion
        let status = 'processing';
        let outputUrl = '';

        const pollStart = Date.now();
        while (status !== 'succeeded' && status !== 'failed') {
          if (Date.now() - pollStart > 180000) throw new Error('Upscale timed out');
          await new Promise(r => setTimeout(r, 2000));
          const pollRes = await studioApiService.pollUpscaleStatus(replicateKey, predictionId);
          status = pollRes.status;
          if (status === 'succeeded') {
            outputUrl = Array.isArray(pollRes.output) ? pollRes.output[0] : pollRes.output;
          }
        }

        if (status === 'succeeded' && outputUrl) {
          // Mock 4 quadrants from the upscaled image output
          const quadrants = Array(4).fill(outputUrl);
          updateUpscalerItem(item.id, { status: 'complete', quadrants });
        } else {
          updateUpscalerItem(item.id, { status: 'error', error: 'Upscale failed' });
        }
      } catch (err: any) {
        updateUpscalerItem(item.id, { status: 'error', error: err.message });
      }
    }

    setIsUpscaling(false);
  };

  const handleUploadS3 = async (item: typeof upscalerQueue[0]) => {
    if (!item.productId || item.productId === 'Unassigned') {
      alert("Please assign a Product ID before uploading!");
      return;
    }
    if (!item.quadrants || item.quadrants.length < 4) {
      alert("Quadrants are not ready yet.");
      return;
    }

    setUploadingId(item.id);
    try {
      const res = await studioApiService.uploadToS3(item.productId, item.quadrants);
      if (res.success) {
        addDbUpload({
          rowId: item.productId,
          imageUrls: res.uploaded,
          status: 'pending'
        });
        alert(`Uploaded ${item.productId} to S3! Go to the DB Upload tab to finish.`);
      }
    } catch (e: any) {
      alert(`S3 Upload failed: ${e.message}`);
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div id="upscalerPage" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="up-body">
        {/* Sidebar */}
        <div className="up-sidebar">
          <div className="panel-scroll">
            <div className="section">
              <div className="section-label">API key — Replicate</div>
              <div className="key-wrap">
                <input 
                  type={showRep ? 'text' : 'password'}
                  value={replicateKey}
                  onChange={e => setReplicateKey(e.target.value)}
                  placeholder="r8_… (optional override)"
                />
                <button className="key-toggle" onClick={() => setShowRep(!showRep)}>
                  {showRep ? 'hide' : 'show'}
                </button>
              </div>
            </div>

            <div className="section">
              <div className="section-label">Selection Controls</div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <button 
                  className="row-dl-btn" 
                  style={{ flex: 1 }} 
                  onClick={() => selectAllUpscaler(true)}
                  disabled={upscalerQueue.length === 0}
                >
                  Select All
                </button>
                <button 
                  className="row-dl-btn" 
                  style={{ flex: 1 }} 
                  onClick={() => selectAllUpscaler(false)}
                  disabled={upscalerQueue.length === 0}
                >
                  Deselect All
                </button>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text3)' }}>
                {selectedItems.length} of {upscalerQueue.length} items selected for upscale
              </div>
            </div>
          </div>

          <div className="run-controls">
            <button 
              className="run-btn primary" 
              onClick={handleUpscaleSelected} 
              disabled={isUpscaling || selectedItems.length === 0}
            >
              {isUpscaling ? 'Upscaling...' : `▶ Upscale Selected (${selectedItems.length})`}
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="up-results">
          <div className="up-results-header">
            <div className="up-results-title">
              Upscaler Queue ({upscalerQueue.length})
            </div>
            <div className="tabs-actions">
              <button className="action-btn" onClick={clearUpscalerQueue}>Clear all</button>
            </div>
          </div>

          <div className="results-area">
            {upscalerQueue.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">⬆</div>
                <div className="empty-title">No images in upscaler</div>
                <div className="empty-sub">Approve generated images in the Image Generator tab to send them here directly!</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {upscalerQueue.map(item => (
                  <div key={item.id} className="result-row" style={{ border: item.selected ? '1px solid var(--accent)' : '1px solid var(--border)' }}>
                    <div className="result-row-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input 
                          type="checkbox" 
                          checked={item.selected} 
                          onChange={() => toggleUpscalerSelect(item.id)}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <div className="result-row-id">
                          Product ID: 
                          <input 
                            type="text" 
                            className="pid-input" 
                            style={{ width: '120px', marginLeft: '6px', padding: '2px 6px' }}
                            value={item.productId}
                            onChange={e => updateUpscalerItem(item.id, { productId: e.target.value })}
                          />
                        </div>
                      </div>
                      <span className={`status-badge ${item.status}`}>{item.status}</span>
                    </div>

                    <div className="result-row-body" style={{ alignItems: 'center' }}>
                      <div className="img-cell">
                        <div className="img-cell-label">Original Grid</div>
                        <img src={`data:image/png;base64,${item.fullSrc}`} className="row-img row-img-full" alt="" />
                      </div>

                      <div className="row-arrow">→</div>

                      <div className="img-cell">
                        <div className="img-cell-label">4× Upscaled Quadrants</div>
                        {item.status === 'complete' && item.quadrants ? (
                          <div className="quad-grid">
                            {item.quadrants.map((q, i) => (
                              <img key={i} src={q} className="row-img row-img-quad" alt="" />
                            ))}
                          </div>
                        ) : item.status === 'upscaling' ? (
                          <div className="skel-img row-img-full">
                            <div className="skel-inner">
                              <div className="dots"><span></span><span></span><span></span></div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ width: '110px', height: '110px', border: '1px dashed var(--border2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'var(--text3)' }}>
                            Pending upscale
                          </div>
                        )}
                      </div>

                      <div className="row-actions" style={{ marginLeft: 'auto' }}>
                        {item.status === 'complete' && (
                          <button 
                            className="row-act-btn" 
                            style={{ background: 'var(--blue)', color: '#fff' }}
                            onClick={() => handleUploadS3(item)}
                            disabled={uploadingId === item.id}
                          >
                            {uploadingId === item.id ? 'Uploading...' : '↑ Upload to S3'}
                          </button>
                        )}
                        {item.error && <div className="row-error">{item.error}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
